//= Functions & Modules
// Own
import { Client, getGlobalClient, setGlobalClient, ConnectStatusChangedEventName } from './modules/server';
import { setDialogsSystem } from './modules/dialogs';
import { useConstructor } from './utils/useConstructor';
import { PermissionsContext } from './modules/permissions';
// Others
import React, { useEffect, useRef, useState } from 'react';

//= Structures & Data
// Own
import { URLs } from './data/URLs';
import { ShowConnectDialogEventName } from './modules/server';

//= React components
// Own
import Top from './components/Top';
import AppMenu from './components/AppMenu';
import BottomNav from './components/BottomNav';
import { DialogsSystem } from './modules/dialogs';
import { NotificationsSystem, setNotificationsSystem } from './modules/notifications';
// Others
import { Suspense } from 'react';
import { Routes, Route } from 'react-router-dom';
import XALUI from '@softprovider/xalui';

//= Style & Assets
// Own
import './styles/global.css';

const ProgramMakerPage = React.lazy(() => import('./modules/program_maker'));
const BotsPage = React.lazy(() => import('./modules/bots/pages/BotsPage'));
const BotPage = React.lazy(() => import('./modules/bots/modules/bot/pages/BotPage'));
const BinanceAccountsListPage = React.lazy(() =>
    import('./modules/binance_accounts/pages/BinanceAccountsList').then((module) => ({ default: module.BinanceAccountsList }))
);
const TradingProgramsListPage = React.lazy(() => import('./modules/trading_programs/pages/TradingProgramsList'));
const SimulatePage = React.lazy(() => import('./modules/simulate/pages/SimulatePage'));

export function App() {
    const dialogsSystemRef = useRef<DialogsSystem | null>(null);
    const notificationsSystemRef = useRef<NotificationsSystem | null>(null);
    const [permissions, setPermissions] = useState<string[]>([]);

    const connectToServerWithRefreshToken = async () => {
        const client = getGlobalClient();

        try {
            const connected = await client.connect();

            if (connected) await client.loginWithRefreshToken();
        } catch (error) {
            console.error(error);
        }

        console.log('DONE');
    };

    const showConnectDialog = () => {
        dialogsSystemRef.current.showDialog(() => import('./modules/server/components/ConnectDialog'));
    };

    useConstructor(() => {
        setGlobalClient(new Client());
        connectToServerWithRefreshToken();
    });

    useEffect(() => {
        setDialogsSystem(dialogsSystemRef.current);
        setNotificationsSystem(notificationsSystemRef.current);
        window.addEventListener(ShowConnectDialogEventName, showConnectDialog);

        const onConnectionStatusChanged = async (event: CustomEvent) => {
            if (event.detail) {
                const perms = await getGlobalClient().getPermissions();
                setPermissions(perms);
            } else {
                setPermissions([]);
            }
        };
        window.addEventListener(ConnectStatusChangedEventName, onConnectionStatusChanged as EventListener);

        return () => {
            window.removeEventListener(ShowConnectDialogEventName, showConnectDialog);
            window.removeEventListener(ConnectStatusChangedEventName, onConnectionStatusChanged as EventListener);
        };
    }, []);

    return (
        <PermissionsContext.Provider value={permissions}>
        <XALUI.ThemeProvider>
            <div className="flex flex-col w-full h-full">
                <Top />
                <div className="flex flex-1 overflow-y-hidden">
                    <AppMenu />
                    <div className="flex-1 w-full p-4 overflow-y-auto">
                        <Suspense fallback={<div className="text-unselected1 text-sm p-4">Loading…</div>}>
                            <Routes>
                                <Route path={URLs.PROGRAM_MAKER} element={<ProgramMakerPage />} />
                                <Route path={URLs.BOTS} element={<BotsPage />} />
                                <Route path={URLs.BOT} element={<BotPage />} />
                                <Route path={URLs.BINANCE_ACCOUNTS} element={<BinanceAccountsListPage />} />
                                <Route path={URLs.TRADING_PROGRAMS} element={<TradingProgramsListPage />} />
                                <Route path={URLs.SIMULATE} element={<SimulatePage />} />
                            </Routes>
                        </Suspense>
                    </div>
                </div>
                <BottomNav />
                <DialogsSystem ref={dialogsSystemRef} />
                <NotificationsSystem ref={notificationsSystemRef} />
            </div>
        </XALUI.ThemeProvider>
        </PermissionsContext.Provider>
    );
}
